import React, { useEffect, useState, useMemo } from 'react';
import { Card, Skeleton, Typography, Tooltip } from 'antd';
import {
  InfoCircleOutlined,
  QuestionCircleOutlined,
  EyeOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import apiClient from '@/api/client';
import type { WidgetProps } from '../registry';
import dayjs from 'dayjs';
import 'leaflet/dist/leaflet.css';
import './SimpleInfoPanel.css';

const { Text } = Typography;

// Custom airport marker icon
const airportIcon = L.divIcon({
  className: 'airport-marker-icon',
  html: `<div style="
    width: 24px;
    height: 24px;
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
    border: 2px solid white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  ">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
      <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
    </svg>
  </div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Component that fits map to bounds after tooltip becomes visible
interface MapBoundsFitterProps {
  bounds: L.LatLngBoundsExpression | null;
}

const MapBoundsFitter: React.FC<MapBoundsFitterProps> = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (!bounds) return;

    // Wait for container to be fully visible (tooltip animation)
    const timer = setTimeout(() => {
      map.invalidateSize();
      map.fitBounds(bounds, {
        padding: [60, 60], // 60px padding on all sides (~20% margin)
        maxZoom: 12, // Don't zoom in too much for short routes
        animate: false, // No animation in tooltip
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [map, bounds]);

  return null;
};

// Legacy component for single point maps
const MapResizer: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 300);

    return () => clearTimeout(timer);
  }, [map]);

  return null;
};

// Icon mapping
const ICONS: Record<string, React.FC<{ style?: React.CSSProperties }>> = {
  info: InfoCircleOutlined,
  help: QuestionCircleOutlined,
  eye: EyeOutlined,
  plane: RocketOutlined,
};

interface InfoPanelProps extends WidgetProps {
  formData?: Record<string, any>;
}

interface DisplayRow {
  label: string;
  field?: string;
  fields?: string[];
  format?: string;
  fallback?: string;
}

interface DisplaySection {
  title: string;
  rows: DisplayRow[];
}

// Helper to get nested value from object using dot notation (e.g., "departure.icao")
const getNestedValue = (obj: any, path: string): any => {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
};

export const SimpleInfoPanel: React.FC<InfoPanelProps> = ({
  widget,
  formData = {},
}) => {
  const [data, setData] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sourceData = widget.source_data;
  const displayTemplate = widget.display_template;

  // Extract field values that are used in URL placeholders
  const urlTemplate = sourceData?.url || '';

  // Find all placeholders in URL like {field_name}
  const placeholders = useMemo(() => {
    const matches = urlTemplate.match(/\{(\w+)\}/g) || [];
    return matches.map((m: string) => m.slice(1, -1));
  }, [urlTemplate]);

  // Get current values for placeholders
  const placeholderValues = useMemo(() => {
    const values: Record<string, string | null> = {};
    placeholders.forEach((fieldName: string) => {
      const field = formData[fieldName];
      values[fieldName] = field?.value ?? null;
    });
    return values;
  }, [placeholders, formData]);

  // Get date from start_time field for query param
  const dateParam = useMemo(() => {
    const startTime = formData.start_time?.value;
    if (!startTime) return null;
    return dayjs(startTime).format('YYYY-MM-DD');
  }, [formData.start_time?.value]);

  // Build final URL with substituted placeholders
  const finalUrl = useMemo(() => {
    if (!urlTemplate) return null;

    // Check if all required placeholders have values
    const missingPlaceholder = placeholders.find(
      (p: string) => !placeholderValues[p]
    );
    if (missingPlaceholder) return null;

    // Substitute placeholders
    let url = urlTemplate;
    placeholders.forEach((fieldName: string) => {
      url = url.replace(`{${fieldName}}`, placeholderValues[fieldName] || '');
    });

    // Add date query param if available
    if (dateParam) {
      const separator = url.includes('?') ? '&' : '?';
      url = `${url}${separator}date=${dateParam}`;
    }

    return url;
  }, [urlTemplate, placeholders, placeholderValues, dateParam]);

  // Fetch data when URL changes
  useEffect(() => {
    if (!finalUrl) {
      setData(null);
      return;
    }

    setLoading(true);
    setError(null);

    apiClient
      .get(finalUrl)
      .then((response) => {
        setData(response.data);
      })
      .catch((err) => {
        console.error('Failed to fetch info panel data:', err);
        setError('Failed to load data');
        setData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [finalUrl]);

  // Format a template string with data values
  const formatTemplate = (
    template: string,
    data: Record<string, any>
  ): string => {
    return template.replace(/\{(\w+)\}/g, (_, field) => data[field] ?? '');
  };

  // Format a row value based on template (supports nested fields like "departure.icao")
  const formatRowValue = (
    row: DisplayRow,
    data: Record<string, any>
  ): string => {
    if (row.format && row.fields) {
      // Use format string with multiple fields
      // Support both {field} and {{field}} syntax
      let result = row.format;
      row.fields.forEach((fieldName) => {
        const value = getNestedValue(data, fieldName);
        // Replace both {field} and {{field}} syntax
        result = result.replace(
          `{${fieldName}}`,
          value ?? row.fallback ?? 'N/A'
        );
        result = result.replace(
          `{{${fieldName}}}`,
          value ?? row.fallback ?? 'N/A'
        );
      });
      return result;
    } else if (row.format && row.field) {
      // Single field with format
      const value = getNestedValue(data, row.field);
      if (value == null) return row.fallback ?? 'N/A';
      // Replace both {field} and {{field}} syntax
      let result = row.format.replace(`{${row.field}}`, value);
      result = result.replace(`{{${row.field}}}`, value);
      return result;
    } else if (row.field) {
      // Single field without format (supports nested paths)
      const value = getNestedValue(data, row.field);
      return value ?? row.fallback ?? 'N/A';
    }
    return row.fallback ?? 'N/A';
  };

  // Don't render if no URL can be built (missing dependencies)
  if (!finalUrl && !loading) {
    return null;
  }

  // Convert css_style from backend (kebab-case) to React style (camelCase)
  const cssStyle = widget.css_style || {};
  const convertedStyle: React.CSSProperties = {};
  Object.entries(cssStyle).forEach(([key, value]) => {
    // Convert kebab-case to camelCase
    const camelKey = key.replace(/-([a-z])/g, (_, letter) =>
      letter.toUpperCase()
    );
    (convertedStyle as any)[camelKey] = value;
  });

  // Get display mode (default: inline)
  const displayMode = widget.display_mode || 'inline';
  const Icon = ICONS[widget.compact_icon || 'info'] || InfoCircleOutlined;

  // Check if we have valid coordinates for the map (single point or GeoJSON route)
  const hasCoordinates = data?.latitude && data?.longitude;

  // Check for departure/arrival coordinates (always needed for route display)
  const departureCoords = data?.departure
    ? {
        lat: data.departure.latitude,
        lng: data.departure.longitude,
        icao: data.departure.icao || data.departure.code || '',
        name: data.departure.name || '',
      }
    : null;

  const arrivalCoords = data?.arrival
    ? {
        lat: data.arrival.latitude,
        lng: data.arrival.longitude,
        icao: data.arrival.icao || data.arrival.code || '',
        name: data.arrival.name || '',
      }
    : null;

  const hasRouteData =
    departureCoords?.lat &&
    departureCoords?.lng &&
    arrivalCoords?.lat &&
    arrivalCoords?.lng;

  // Calculate great circle (geodesic) points between two coordinates
  const calculateGreatCirclePoints = (
    start: { lat: number; lng: number },
    end: { lat: number; lng: number },
    numPoints: number = 100
  ): [number, number][] => {
    const points: [number, number][] = [];

    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const toDeg = (rad: number) => (rad * 180) / Math.PI;

    const lat1 = toRad(start.lat);
    const lon1 = toRad(start.lng);
    const lat2 = toRad(end.lat);
    const lon2 = toRad(end.lng);

    // Calculate the angular distance
    const d =
      2 *
      Math.asin(
        Math.sqrt(
          Math.pow(Math.sin((lat2 - lat1) / 2), 2) +
            Math.cos(lat1) *
              Math.cos(lat2) *
              Math.pow(Math.sin((lon2 - lon1) / 2), 2)
        )
      );

    for (let i = 0; i <= numPoints; i++) {
      const f = i / numPoints;

      const A = Math.sin((1 - f) * d) / Math.sin(d);
      const B = Math.sin(f * d) / Math.sin(d);

      const x =
        A * Math.cos(lat1) * Math.cos(lon1) +
        B * Math.cos(lat2) * Math.cos(lon2);
      const y =
        A * Math.cos(lat1) * Math.sin(lon1) +
        B * Math.cos(lat2) * Math.sin(lon2);
      const z = A * Math.sin(lat1) + B * Math.sin(lat2);

      const lat = Math.atan2(z, Math.sqrt(x * x + y * y));
      const lon = Math.atan2(y, x);

      points.push([toDeg(lat), toDeg(lon)]);
    }

    return points;
  };

  // Get great circle route points
  const greatCirclePoints = hasRouteData
    ? calculateGreatCirclePoints(departureCoords!, arrivalCoords!, 100)
    : [];

  // Calculate bounds using only the two airport coordinates
  const getRouteBounds = (): L.LatLngBoundsExpression | null => {
    if (!hasRouteData) return null;

    // Simple bounds from two points - fitBounds will handle the rest
    return L.latLngBounds([
      [departureCoords!.lat, departureCoords!.lng],
      [arrivalCoords!.lat, arrivalCoords!.lng],
    ]);
  };

  // Create airport marker icon
  const createAirportIcon = (icao: string, type: 'departure' | 'arrival') => {
    return L.divIcon({
      className: 'route-airport-marker',
      html: `<div class="route-marker-content ${type}">${icao}</div>`,
      iconSize: [70, 28],
      iconAnchor: [35, 14],
    });
  };

  // Tooltip content component
  const TooltipContent = () => {
    if (loading) {
      return (
        <div className="info-panel-tooltip-loading">
          <Skeleton active paragraph={{ rows: 4 }} title={false} />
        </div>
      );
    }

    if (error) {
      return <div className="info-panel-tooltip-error">{error}</div>;
    }

    // Support both rows and sections format
    const hasRows = displayTemplate?.rows && displayTemplate.rows.length > 0;
    const hasSections =
      displayTemplate?.sections && displayTemplate.sections.length > 0;

    if (!data || (!hasRows && !hasSections)) {
      return null;
    }

    // Format title if available
    const title = displayTemplate.title
      ? formatTemplate(displayTemplate.title, data)
      : widget.label || 'Information';

    const showMap = hasCoordinates || hasRouteData;
    const routeBounds = getRouteBounds();

    return (
      <div
        className={`info-panel-tooltip-content ${showMap ? 'with-map' : ''}`}
      >
        <div className="info-panel-tooltip-header">{title}</div>
        <div className="info-panel-tooltip-main">
          {/* Info section - rows format */}
          {hasRows && (
            <div className="info-panel-tooltip-body">
              {displayTemplate.rows!.map((row: DisplayRow, index: number) => (
                <div key={index} className="info-panel-tooltip-row">
                  <span className="info-panel-tooltip-label">{row.label}:</span>
                  <span className="info-panel-tooltip-value">
                    {formatRowValue(row, data)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Info section - sections format */}
          {hasSections && (
            <div className="info-panel-tooltip-body with-sections">
              {displayTemplate.sections!.map(
                (section: DisplaySection, sectionIndex: number) => (
                  <div key={sectionIndex} className="info-panel-section">
                    <div className="info-panel-section-title">
                      {section.title}
                    </div>
                    {section.rows.map((row: DisplayRow, rowIndex: number) => (
                      <div key={rowIndex} className="info-panel-tooltip-row">
                        <span className="info-panel-tooltip-label">
                          {row.label}:
                        </span>
                        <span className="info-panel-tooltip-value">
                          {formatRowValue(row, data)}
                        </span>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          )}

          {/* Map section - single point marker (for single airport info) */}
          {hasCoordinates && !hasRouteData && (
            <div className="info-panel-tooltip-map">
              <MapContainer
                center={[data.latitude, data.longitude]}
                zoom={10}
                scrollWheelZoom={false}
                zoomControl={false}
                dragging={false}
                doubleClickZoom={false}
              >
                <MapResizer />
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker
                  position={[data.latitude, data.longitude]}
                  icon={airportIcon}
                />
              </MapContainer>
            </div>
          )}

          {/* Map section - Flight route with great circle path */}
          {hasRouteData && routeBounds && (
            <div className="info-panel-tooltip-map route-map">
              <MapContainer
                center={[0, 0]}
                zoom={2}
                scrollWheelZoom={false}
                zoomControl={false}
                dragging={false}
                doubleClickZoom={false}
              >
                <MapBoundsFitter bounds={routeBounds} />
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {/* Great circle route line */}
                <Polyline
                  positions={greatCirclePoints}
                  pathOptions={{
                    color: '#3b82f6',
                    weight: 3,
                    opacity: 0.9,
                  }}
                />
                {/* Departure marker */}
                <Marker
                  position={[departureCoords!.lat, departureCoords!.lng]}
                  icon={createAirportIcon(departureCoords!.icao, 'departure')}
                />
                {/* Arrival marker */}
                <Marker
                  position={[arrivalCoords!.lat, arrivalCoords!.lng]}
                  icon={createAirportIcon(arrivalCoords!.icao, 'arrival')}
                />
              </MapContainer>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Tooltip mode - compact trigger with popup on hover/click
  if (displayMode === 'tooltip') {
    return (
      <Tooltip
        title={<TooltipContent />}
        trigger={widget.tooltip_trigger === 'click' ? 'click' : 'hover'}
        placement="bottomLeft"
        overlayClassName="info-panel-tooltip-overlay"
        color="transparent"
        overlayInnerStyle={{
          padding: 0,
          background: 'transparent',
          boxShadow: 'none',
        }}
      >
        <div className="info-panel-trigger" style={convertedStyle}>
          <Icon style={{ fontSize: '16px' }} />
          {widget.compact_text && (
            <span className="info-panel-trigger-text">
              {widget.compact_text}
            </span>
          )}
        </div>
      </Tooltip>
    );
  }

  // Inline mode - full card display (default)
  return (
    <Card
      size="small"
      className="rul-info-panel"
      style={{
        marginBottom: '1rem',
        backgroundColor: 'var(--bg-secondary, rgba(255, 255, 255, 0.05))',
        borderRadius: '8px',
        ...convertedStyle,
      }}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}
      >
        <InfoCircleOutlined
          style={{ marginRight: '8px', color: 'var(--primary-color, #1890ff)' }}
        />
        <Text strong>{widget.label || 'Information'}</Text>
      </div>

      {loading && (
        <Skeleton
          active
          paragraph={{ rows: 4, width: ['60%', '80%', '70%', '50%'] }}
          title={false}
        />
      )}

      {error && (
        <Text type="danger" style={{ fontSize: '12px' }}>
          {error}
        </Text>
      )}

      {!loading && !error && data && displayTemplate?.rows && (
        <div style={{ fontSize: '13px' }}>
          {displayTemplate.rows.map((row: DisplayRow, index: number) => (
            <div
              key={index}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '4px 0',
                borderBottom:
                  index < displayTemplate.rows.length - 1
                    ? '1px solid var(--border-color, #e8e8e8)'
                    : 'none',
              }}
            >
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {row.label}:
              </Text>
              <Text style={{ fontSize: '12px', textAlign: 'right' }}>
                {formatRowValue(row, data)}
              </Text>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
